import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useState,
  useId,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import {
  listTemplatesForCurrentUser,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type NewSessionTemplate,
} from '@lib/templates';
import type {
  SessionTemplate,
  BreathingStyleRecord,
  BreathingRatio,
  BackgroundVideo,
  VisionBoardImage,
} from '@lib/api';
import {
  BREATHING_STYLE_LABELS,
  DEFAULT_BREATHING_STYLE,
  isBreathingStyle,
} from '@/types/breathing';
import type { BreathingStyle } from '@/types/breathing';
import { listBreathingStyles } from '@lib/breathingStyles';
import { listBreathingRatios } from '@lib/breathingRatio';
import { listBackgroundVideos } from '@lib/backgroundVideos';
import { supabase } from '@lib/supabase';

interface TemplateFormState {
  template_name: string;
  voice_guidance: boolean;
  phase_chime: boolean;
  background_sound: boolean;
  intro_voice: boolean;
  // New options to hide UI elements during breathing
  hide_cycle_count: boolean;
  hide_time_left: boolean;
  // New option to control whether the template allows background play
  background_play: boolean;
  duration: string;
  breathing_ratio_id: string;
  breathing_ratio_other_inhale: string;
  breathing_ratio_other_hold_full: string;
  breathing_ratio_other_exhale: string;
  breathing_ratio_other_hold_empty: string;
  background_videos_id: string;
  vision_board_id: string;
  breathing_style_choice: BreathingStyle;
  breathing_style_id: string;
  music_enabled: boolean;
  music_track_id: string;
  affirmation: boolean;
  affirmation_type: string;
  affirmation_mode: 'regular' | 'subliminal';
  affirmation_whisper_mode: boolean;
}

type BooleanTemplateField =
  | 'voice_guidance'
  | 'phase_chime'
  | 'background_sound'
  | 'intro_voice'
  | 'background_play'
  | 'hide_cycle_count'
  | 'hide_time_left';

type SelectField =
  | 'breathing_ratio_id'
  | 'background_videos_id'
  | 'vision_board_id'
  | 'breathing_style_id';

type CustomRatioField =
  | 'breathing_ratio_other_inhale'
  | 'breathing_ratio_other_hold_full'
  | 'breathing_ratio_other_exhale'
  | 'breathing_ratio_other_hold_empty';

const blankForm: TemplateFormState = {
  template_name: '',
  voice_guidance: true,
  phase_chime: true,
  background_sound: true,
  intro_voice: true,
  hide_cycle_count: false,
  hide_time_left: false,
  duration: '',
  breathing_ratio_id: '',
  breathing_ratio_other_inhale: '',
  breathing_ratio_other_hold_full: '',
  breathing_ratio_other_exhale: '',
  breathing_ratio_other_hold_empty: '',
  background_videos_id: '',
  vision_board_id: '',
  breathing_style_choice: DEFAULT_BREATHING_STYLE,
  breathing_style_id: '',
  music_enabled: false,
  music_track_id: '',
  affirmation: false,
  affirmation_type: '',
  affirmation_mode: 'regular',
  affirmation_whisper_mode: false,
  background_play: true,
};

const BREATHING_STYLE_SELECT_OPTIONS = Object.entries(
  BREATHING_STYLE_LABELS
) as Array<[BreathingStyle, string]>;

const AFFIRMATION_TYPE_OPTIONS = [
  { value: 'panic_attack_relief', label: 'Panic Attack Relief' },
  { value: 'calm_anxiety', label: 'Calm & Anxiety Relief' },
  { value: 'confidence_selfworth', label: 'Confidence & Self-Worth' },
  { value: 'money_abundance', label: 'Money & Abundance' },
  { value: 'love_relationships', label: 'Love & Relationships' },
  { value: 'health_healing', label: 'Health & Healing' },
  { value: 'focus_clarity', label: 'Focus & Clarity' },
  { value: 'gratitude', label: 'Gratitude' },
  { value: 'sleep_deep_rest', label: 'Sleep & Deep Rest' },
] as const;

const getAffirmationTypeLabel = (id: string | null | undefined) => {
  if (!id) return '—';
  const found = AFFIRMATION_TYPE_OPTIONS.find((o) => o.value === id);
  return found?.label || 'Unknown';
};

const sortByName = (list: SessionTemplate[]) =>
  [...list].sort((a, b) => a.template_name.localeCompare(b.template_name));

const numberToInputValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : String(parsed);
};

const parseIntegerOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.trunc(parsed);
};

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  const titleId = useId();

  return (
    <div className="fixed inset-0 z-50 overflow-y-hidden">
      <div className="relative z-10 flex min-h-full items-start justify-center px-4 pb-8 pt-4 sm:pb-12 sm:pt-6">
        <div
          className="fixed inset-0 z-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative z-10 max-h-[95vh] w-full max-w-xl overflow-hidden rounded-2xl bg-white/95 p-5 shadow-2xl backdrop-blur sm:p-6"
        >
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h2 id={titleId} className="text-xl font-semibold text-gray-900">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="self-end rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <span className="sr-only">Close</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M6 18L18 6"
                />
              </svg>
            </button>
          </div>
          <div className="max-h-[calc(95vh-4rem)] overflow-y-auto pb-6 pr-3">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionsMenu(props: {
  label?: string;
  side?: 'top' | 'bottom';
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items, side = 'bottom' } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div
          className={`absolute right-0 z-50 w-48 rounded-2xl border border-gray-100 bg-white shadow-lg ${
            side === 'top'
              ? 'bottom-full mb-2 origin-bottom-right'
              : 'top-full mt-2 origin-top-right'
          }`}
        >
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SessionTemplates() {
  const [templates, setTemplates] = useState<SessionTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<SessionTemplate | null>(
    null
  );
  const [formValues, setFormValues] = useState<TemplateFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [breathingStylesFromDb, setBreathingStylesFromDb] = useState<
    BreathingStyleRecord[]
  >([]);
  const [breathingRatios, setBreathingRatios] = useState<BreathingRatio[]>([]);
  const [backgroundVideos, setBackgroundVideos] = useState<BackgroundVideo[]>(
    []
  );
  const [visionBoardImages, setVisionBoardImages] = useState<
    VisionBoardImage[]
  >([]);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await listBreathingStyles();
        if (!active) return;
        setBreathingStylesFromDb(list || []);
      } catch (e) {
        // ignore optional feature
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const [ratioList, videoList] = await Promise.all([
          listBreathingRatios(true),
          listBackgroundVideos(true),
        ]);
        if (!active) return;
        setBreathingRatios(ratioList || []);
        setBackgroundVideos(videoList || []);
      } catch (e) {
        // Optional reference data; silently ignore failures
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data: userRes } = await supabase.auth.getUser();
        const user = userRes.user;
        if (!user) return;

        const { data, error } = await supabase
          .from('vision_board_images')
          .select('id, image_url, preview_image_url, title, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (!active) return;
        if (error) throw error;

        setVisionBoardImages((data as VisionBoardImage[]) || []);
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const getBreathingRatioName = (id: string | null | undefined) => {
    if (!id) return '—';
    const found = breathingRatios.find((ratio) => ratio.id === id);
    return found?.name || 'Unknown';
  };

  const getBackgroundVideoName = (id: string | null | undefined) => {
    if (!id) return '—';
    const found = backgroundVideos.find((video) => video.id === id);
    return found?.name || 'Unknown';
  };

  useEffect(() => {
    let cancelled = false;

    async function loadTemplates() {
      try {
        setLoading(true);
        const data = await listTemplatesForCurrentUser();
        if (!cancelled) {
          setTemplates(sortByName(data));
          setError(null);
        }
      } catch (_error) {
        if (!cancelled) {
          setError('Unable to load templates right now. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, template_name: event.target.value }));
  };

  const handleToggleChange =
    (field: BooleanTemplateField) => (event: ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.checked }));
    };

  const handleDurationChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, duration: event.target.value }));
  };

  const handleSelectChange =
    (field: SelectField) => (event: ChangeEvent<HTMLSelectElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleCustomRatioChange =
    (field: CustomRatioField) => (event: ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleBreathingStyleChoiceChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      breathing_style_choice: isBreathingStyle(value)
        ? value
        : DEFAULT_BREATHING_STYLE,
    }));
  };

  const openFormModalWithTemplate = (template?: SessionTemplate | null) => {
    if (template) {
      setActiveTemplate(template);
      const hasCustomRatio = Boolean(
        !template.breathing_ratio_id &&
          (template.breathing_ratio_other_inhale !== null ||
            template.breathing_ratio_other_hold_full !== null ||
            template.breathing_ratio_other_exhale !== null ||
            template.breathing_ratio_other_hold_empty !== null)
      );
      const breathingStyleChoice = isBreathingStyle(template.breathing_style)
        ? template.breathing_style
        : DEFAULT_BREATHING_STYLE;

      setFormValues({
        template_name: template.template_name || '',
        voice_guidance: Boolean(template.voice_guidance),
        phase_chime: Boolean(template.phase_chime),
        background_sound: Boolean(template.background_sound),
        intro_voice: Boolean(template.intro_voice),
        hide_cycle_count: Boolean(template.hide_cycle_count),
        hide_time_left: Boolean(template.hide_time_left),
        background_play: Boolean(
          (template as any).background_play ?? template.play_in_background
        ),
        duration: numberToInputValue(template.duration),
        breathing_ratio_id:
          template.breathing_ratio_id || (hasCustomRatio ? 'custom' : ''),
        breathing_ratio_other_inhale: numberToInputValue(
          template.breathing_ratio_other_inhale ?? null
        ),
        breathing_ratio_other_hold_full: numberToInputValue(
          template.breathing_ratio_other_hold_full ?? null
        ),
        breathing_ratio_other_exhale: numberToInputValue(
          template.breathing_ratio_other_exhale ?? null
        ),
        breathing_ratio_other_hold_empty: numberToInputValue(
          template.breathing_ratio_other_hold_empty ?? null
        ),
        background_videos_id: template.background_videos_id || '',
        vision_board_id: template.vision_board_id || '',
        breathing_style_choice: breathingStyleChoice,
        breathing_style_id: template.breathing_style_id || '',
        music_enabled: Boolean(template.music_enabled),
        music_track_id: template.music_track_id || '',
        affirmation: Boolean(template.affirmation),
        affirmation_type: template.affirmation
          ? template.affirmation_type || ''
          : '',
        affirmation_mode: template.affirmation_mode || 'regular',
        affirmation_whisper_mode: Boolean(template.affirmation_whisper_mode),
      });
    } else {
      setActiveTemplate(null);
      setFormValues({ ...blankForm });
    }

    setFormError(null);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsSubmitting(false);
    setActiveTemplate(null);
    setFormValues({ ...blankForm });
  };

  const submitTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const trimmedName = formValues.template_name.trim();
    if (!trimmedName) {
      setFormError('Template name is required.');
      return;
    }

    const durationValue = parseIntegerOrNull(formValues.duration);
    if (durationValue !== null && durationValue <= 0) {
      setFormError('Duration must be a positive number.');
      return;
    }

    const isCustomRatio = formValues.breathing_ratio_id === 'custom';
    const selectedRatioId =
      formValues.breathing_ratio_id && !isCustomRatio
        ? formValues.breathing_ratio_id
        : null;
    const backgroundVideoId = formValues.background_videos_id.trim();
    const visionBoardId = formValues.vision_board_id.trim();
    const breathingStyleId = formValues.breathing_style_id.trim();

    const payload: NewSessionTemplate = {
      template_name: trimmedName,
      voice_guidance: formValues.voice_guidance,
      phase_chime: formValues.phase_chime,
      background_sound: formValues.background_sound,
      music_enabled: formValues.music_enabled,
      music_track_id: formValues.music_enabled
        ? formValues.music_track_id || null
        : null,
      intro_voice: formValues.intro_voice,
      duration: durationValue,
      breathing_ratio_id: selectedRatioId,
      breathing_ratio_other_inhale: isCustomRatio
        ? parseIntegerOrNull(formValues.breathing_ratio_other_inhale)
        : null,
      breathing_ratio_other_hold_full: isCustomRatio
        ? parseIntegerOrNull(formValues.breathing_ratio_other_hold_full)
        : null,
      breathing_ratio_other_exhale: isCustomRatio
        ? parseIntegerOrNull(formValues.breathing_ratio_other_exhale)
        : null,
      breathing_ratio_other_hold_empty: isCustomRatio
        ? parseIntegerOrNull(formValues.breathing_ratio_other_hold_empty)
        : null,
      background_videos_id: backgroundVideoId ? backgroundVideoId : null,
      vision_board_id: visionBoardId ? visionBoardId : null,
      breathing_style: formValues.breathing_style_choice,
      breathing_style_id: breathingStyleId ? breathingStyleId : null,
      affirmation: formValues.affirmation,
      affirmation_type: formValues.affirmation
        ? formValues.affirmation_type || null
        : null,
      affirmation_mode: formValues.affirmation
        ? formValues.affirmation_mode
        : 'regular',
      affirmation_whisper_mode: formValues.affirmation
        ? formValues.affirmation_whisper_mode
        : false,
      hide_cycle_count: formValues.hide_cycle_count,
      hide_time_left: formValues.hide_time_left,
      // Persist background play setting
      background_play: formValues.background_play,
    };

    setIsSubmitting(true);

    try {
      if (activeTemplate) {
        const updated = await updateTemplate(activeTemplate.id, payload);
        setTemplates((prev) =>
          sortByName(
            prev.map((item) => (item.id === updated.id ? updated : item))
          )
        );
      } else {
        const created = await createTemplate(payload);
        setTemplates((prev) => sortByName([...prev, created]));
      }
      setShowFormModal(false);
      setActiveTemplate(null);
      setFormValues({ ...blankForm });
    } catch (_error) {
      setFormError('Unable to save template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (template: SessionTemplate) => {
    setActiveTemplate(template);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleDelete = useCallback(async () => {
    if (!activeTemplate || isDeleting) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteTemplate(activeTemplate.id);
      setTemplates((prev) =>
        prev.filter((item) => item.id !== activeTemplate.id)
      );
      setShowDeleteModal(false);
      setActiveTemplate(null);
    } catch (_error) {
      setDeleteError('Failed to delete template. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [activeTemplate, isDeleting]);

  useEffect(() => {
    if (!showDeleteModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        // If we're focused on the cancel button, let that button's click handle it
        // Otherwise, trigger delete
        if (
          document.activeElement instanceof HTMLButtonElement &&
          document.activeElement.innerText.toLowerCase().includes('cancel')
        ) {
          return;
        }

        e.preventDefault();
        handleDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, handleDelete]);

  const isCustomRatioSelected = formValues.breathing_ratio_id === 'custom';

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="mx-auto max-w-5xl pt-16 text-center">
        <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
          My Session Templates
        </h1>
        <p className="text-lg text-gray-600">
          Keep your favorite breathing patterns ready for any moment.{' '}
        </p>
      </div>

      <div className="relative mx-auto max-w-6xl px-4 py-12">
        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="!bg-teal-500 !text-white shadow-md transition hover:!bg-teal-600 focus:!ring-teal-400"
            >
              + New Template
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No templates yet. Create your first one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 lg:hidden">
                  {templates.map((template, index) => (
                    <div
                      key={template.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-lg font-semibold text-gray-900">
                            {template.template_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {getBreathingRatioName(template.breathing_ratio_id)}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <ActionsMenu
                            side={
                              index > 0 && index === templates.length - 1
                                ? 'top'
                                : 'bottom'
                            }
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () =>
                                  openFormModalWithTemplate(template),
                                className: '!text-indigo-600',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDeleteModal(template),
                                className: '!text-rose-600',
                              },
                            ]}
                          />
                        </div>
                      </div>
                      <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm text-gray-700 [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
                        <div>
                          <dt className="font-medium text-gray-500">
                            Affirmation Type
                          </dt>
                          <dd>
                            {template.affirmation
                              ? getAffirmationTypeLabel(
                                  template.affirmation_type
                                )
                              : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500">
                            Background Video
                          </dt>
                          <dd>
                            {getBackgroundVideoName(
                              template.background_videos_id
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Affirmation Type</th>
                          <th className="px-4 py-3">Breathing Ratio</th>
                          <th className="px-4 py-3">Background Video</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map((template, index) => (
                          <tr
                            key={template.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-semibold text-gray-900">
                                {template.template_name}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-sm text-gray-700">
                              {template.affirmation
                                ? getAffirmationTypeLabel(
                                    template.affirmation_type
                                  )
                                : '—'}
                            </td>
                            <td className="px-4 py-4 align-top text-sm text-gray-700">
                              {getBreathingRatioName(
                                template.breathing_ratio_id
                              )}
                            </td>
                            <td className="px-4 py-4 align-top text-sm text-gray-700">
                              {getBackgroundVideoName(
                                template.background_videos_id
                              )}
                            </td>
                            <td className="px-4 py-4 text-right align-top">
                              <div className="flex flex-wrap justify-end gap-2">
                                <ActionsMenu
                                  side={
                                    index > 0 && index >= templates.length - 2
                                      ? 'top'
                                      : 'bottom'
                                  }
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () =>
                                        openFormModalWithTemplate(template),
                                      className: '!text-indigo-600',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDeleteModal(template),
                                      className: '!text-rose-600',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <Modal
          title={activeTemplate ? 'Edit Template' : 'Create Template'}
          onClose={closeFormModal}
        >
          <form onSubmit={submitTemplate} className="space-y-6">
            <div>
              <label
                htmlFor="template-name"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Template Name
              </label>
              <input
                id="template-name"
                type="text"
                value={formValues.template_name}
                maxLength={80}
                onChange={handleNameChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="e.g. Evening Calm"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="template-duration"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Duration (minutes)
                </label>
                <input
                  id="template-duration"
                  type="number"
                  min={1}
                  value={formValues.duration}
                  onChange={handleDurationChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  placeholder="e.g. 5"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave blank to keep the in-app default.
                </p>
              </div>
              <div>
                <label
                  htmlFor="template-style-choice"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Breathing Style (visual)
                </label>
                <select
                  id="template-style-choice"
                  value={formValues.breathing_style_choice}
                  onChange={handleBreathingStyleChoiceChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {BREATHING_STYLE_SELECT_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Controls the animation shown during breathing sessions.
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="template-ratio"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Breathing Ratio
              </label>
              <select
                id="template-ratio"
                value={formValues.breathing_ratio_id}
                onChange={handleSelectChange('breathing_ratio_id')}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                <option value="">Select a saved ratio…</option>
                {breathingRatios.map((ratio) => (
                  <option key={ratio.id} value={ratio.id}>
                    {ratio.name}
                    {ratio.ratio ? ` (${ratio.ratio})` : ''}
                  </option>
                ))}
                <option value="custom">Custom ratio</option>
              </select>
            </div>

            {isCustomRatioSelected && (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                <p className="mb-4 text-sm font-medium text-gray-700">
                  Custom Ratio Parts (seconds)
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-gray-700">
                    Inhale
                    <input
                      type="number"
                      min={0}
                      value={formValues.breathing_ratio_other_inhale}
                      onChange={handleCustomRatioChange(
                        'breathing_ratio_other_inhale'
                      )}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Hold (full lungs)
                    <input
                      type="number"
                      min={0}
                      value={formValues.breathing_ratio_other_hold_full}
                      onChange={handleCustomRatioChange(
                        'breathing_ratio_other_hold_full'
                      )}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Exhale
                    <input
                      type="number"
                      min={0}
                      value={formValues.breathing_ratio_other_exhale}
                      onChange={handleCustomRatioChange(
                        'breathing_ratio_other_exhale'
                      )}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Hold (empty lungs)
                    <input
                      type="number"
                      min={0}
                      value={formValues.breathing_ratio_other_hold_empty}
                      onChange={handleCustomRatioChange(
                        'breathing_ratio_other_hold_empty'
                      )}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Leave any phase blank if it should inherit the default rhythm.
                </p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="template-background-video"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Background Video
                </label>
                <select
                  id="template-background-video"
                  value={formValues.background_videos_id}
                  onChange={handleSelectChange('background_videos_id')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  <option value="">No background video</option>
                  {backgroundVideos.map((video) => (
                    <option key={video.id} value={video.id}>
                      {video.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="template-vision-board"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Vision Board
                </label>
                <select
                  id="template-vision-board"
                  value={formValues.vision_board_id}
                  onChange={handleSelectChange('vision_board_id')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  <option value="">No vision board</option>
                  {visionBoardImages.map((img) => (
                    <option key={img.id} value={img.id}>
                      {img.title || 'Untitled Vision Board'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="template-style-id"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Breathing Style Preset (database)
                </label>
                <select
                  id="template-style-id"
                  value={formValues.breathing_style_id}
                  onChange={handleSelectChange('breathing_style_id')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  <option value="">No preset override</option>
                  {breathingStylesFromDb.map((style) => (
                    <option key={style.id} value={style.id}>
                      {style.name || 'Untitled style'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Keeps templates aligned with Supabase breathing style entries.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">
                Audio Guidance Options
              </p>
              <div className="space-y-3">
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.voice_guidance}
                    onChange={handleToggleChange('voice_guidance')}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Enable voice guidance prompts
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.intro_voice}
                    onChange={handleToggleChange('intro_voice')}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Play the intro voice before breathing begins
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.phase_chime}
                    onChange={handleToggleChange('phase_chime')}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Play a chime at each phase change
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.background_sound}
                    onChange={handleToggleChange('background_sound')}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Include Ambiant background sound
                </label>

                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.background_play}
                    onChange={handleToggleChange('background_play')}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Enable Background Play (session can continue when tab is
                  inactive)
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!formValues.hide_cycle_count}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        hide_cycle_count: !e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Cycle count during breathing
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!formValues.hide_time_left}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        hide_time_left: !e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Time remaining during breathing
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">Affirmations</p>
              <div className="space-y-3">
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formValues.affirmation}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        affirmation: e.target.checked,
                        affirmation_type: e.target.checked
                          ? prev.affirmation_type
                          : '',
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  Enable affirmations before and after a session
                </label>

                {formValues.affirmation && (
                  <div>
                    <label
                      htmlFor="template-affirmation-type"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Affirmation Type
                    </label>
                    <select
                      id="template-affirmation-type"
                      value={formValues.affirmation_type}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          affirmation_type: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                    >
                      <option value="">Select a type</option>
                      {AFFIRMATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button type="button" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="!bg-teal-500 !text-white hover:!bg-teal-600 focus:!ring-teal-400"
              >
                {isSubmitting
                  ? 'Saving…'
                  : activeTemplate
                    ? 'Save Changes'
                    : 'Create Template'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showDeleteModal && activeTemplate && (
        <Modal title="Delete Template" onClose={closeDeleteModal}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleDelete();
            }}
          >
            <p className="text-sm text-gray-700">
              Are you sure you want to delete{' '}
              <span className="font-semibold">
                {activeTemplate.template_name}
              </span>
              ? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                autoFocus
                disabled={isDeleting}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default SessionTemplates;
